import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Users, Repeat, MapPin, Layers, TrendingUp, ChevronRight, Sparkles } from 'lucide-react';
import Layout from '../components/Layout';
import CompanyFilter from '../components/CompanyFilter';
import { api, formatCurrency } from '../lib/api';

const KPI = ({ label, value, sub, accent = false, testId, delay = '' }) => (
  <div
    data-testid={testId}
    className={`bg-surface border border-border p-5 fade-up ${delay} ${accent ? 'border-l-2 border-l-maroon' : ''}`}
  >
    <p className="font-body text-[10px] uppercase tracking-[0.25em] text-text-secondary font-semibold mb-3">{label}</p>
    <p className="font-numbers text-2xl font-light text-text-primary tabular-nums">{value}</p>
    {sub && <p className="font-body text-xs text-text-secondary mt-2">{sub}</p>}
  </div>
);

const SectionTitle = ({ tag, title, action }) => (
  <div className="flex items-baseline justify-between mb-4">
    <div>
      <p className="font-body text-xs uppercase tracking-[0.2em] text-maroon mb-1">— {tag}</p>
      <h2 className="font-heading text-xl font-light text-text-primary">{title}</h2>
    </div>
    {action}
  </div>
);

const fmtL = (n) => {
  const v = Number(n) || 0;
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1)}k`;
  return `₹${v.toFixed(0)}`;
};

const AnalystDashboard = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/companies').then(r => setCompanies(r.data)).catch(() => setCompanies([]));
  }, []);

  useEffect(() => {
    const params = {};
    if (selectedCompany) params.company_id = selectedCompany;
    setLoading(true);
    api
      .get('/dashboard/analyst-summary', { params })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [selectedCompany]);

  const kpis = data?.kpis || {};
  const segments = data?.segments || [];
  const topCities = data?.top_cities || [];
  const trend = data?.growth_trend || [];
  const upcoming = data?.upcoming_retainers || [];
  const maxSegClients = Math.max(1, ...segments.map(s => s.clients));
  const maxCityClients = Math.max(1, ...topCities.map(c => c.clients));

  return (
    <Layout
      title="Analyst Dashboard"
      action={<CompanyFilter companies={companies} selected={selectedCompany} onChange={setSelectedCompany} />}
    >
      {/* Intro banner */}
      <div data-testid="analyst-banner" className="bg-cream border border-border p-4 mb-6 flex items-start gap-3 fade-up">
        <Sparkles className="w-4 h-4 text-maroon mt-0.5 flex-shrink-0" strokeWidth={1.5} />
        <div>
          <p className="font-body text-xs uppercase tracking-[0.2em] text-maroon mb-1">— Analyst View</p>
          <p className="font-body text-sm text-text-primary">
            Snapshot of client mix and retainer footprint. Financial invoicing and receivables are out of scope for this role.
          </p>
        </div>
      </div>

      {/* KPI row */}
      <div data-testid="analyst-kpis" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <KPI
          testId="kpi-total-clients"
          label="Total Clients"
          value={loading ? '—' : kpis.total_clients ?? 0}
          sub={`${kpis.active_clients ?? 0} active`}
        />
        <KPI
          testId="kpi-new-clients"
          label="New This Month"
          value={loading ? '—' : kpis.new_clients_this_month ?? 0}
          sub="onboarded in current cycle"
          accent
        />
        <KPI
          testId="kpi-retainers"
          label="Active Retainers"
          value={loading ? '—' : kpis.active_retainers ?? 0}
          sub={`${kpis.retainer_coverage_pct ?? 0}% of active clients`}
        />
        <KPI
          testId="kpi-mrr"
          label="Monthly Retainer Value"
          value={loading ? '—' : fmtL(kpis.total_monthly_retainer)}
          sub="normalised MRR across templates"
        />
        <KPI
          testId="kpi-segments"
          label="Client Segments"
          value={loading ? '—' : segments.length}
          sub="distinct client types"
        />
        <KPI
          testId="kpi-cities"
          label="Cities Covered"
          value={loading ? '—' : topCities.length}
          sub="unique markets tracked"
        />
      </div>

      {/* Growth trend + Segment mix */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div data-testid="analyst-trend" className="lg:col-span-2 bg-surface border border-border p-6 fade-up">
          <SectionTitle
            tag="Growth"
            title="New Clients · Last 12 Months"
            action={
              <button
                data-testid="analyst-goto-intel"
                onClick={() => navigate('/client-intelligence')}
                className="font-body text-xs uppercase tracking-widest text-maroon hover:text-maroon-hover"
              >
                Deep Dive →
              </button>
            }
          />
          <div style={{ width: '100%', height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={trend} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                <CartesianGrid stroke="#D4CEC6" strokeDasharray="0" vertical={false} />
                <XAxis
                  dataKey="label"
                  stroke="#4A4A4A"
                  tick={{ fontFamily: 'Outfit', fontSize: 12, fontWeight: 300 }}
                  axisLine={{ stroke: '#D4CEC6' }}
                  tickLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  stroke="#4A4A4A"
                  tick={{ fontFamily: 'EB Garamond', fontSize: 12 }}
                  axisLine={{ stroke: '#D4CEC6' }}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: '#EDE8E0',
                    border: '1px solid #D4CEC6',
                    borderRadius: 0,
                    fontFamily: 'Outfit',
                    fontWeight: 300,
                  }}
                />
                <Bar dataKey="count" fill="#5C1A2A" name="New clients" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div data-testid="analyst-segments" className="bg-surface border border-border p-6 fade-up fade-up-d1">
          <SectionTitle tag="Mix" title="Segments" />
          <div className="space-y-4">
            {segments.length === 0 && !loading && (
              <p className="font-body text-sm text-text-secondary">No segment data yet.</p>
            )}
            {segments.map(s => {
              const pct = (s.clients / maxSegClients) * 100;
              return (
                <div key={s.segment} data-testid={`segment-${s.segment}`}>
                  <div className="flex justify-between mb-1">
                    <span className="font-body text-xs uppercase tracking-widest text-text-secondary flex items-center gap-2">
                      <Layers className="w-3 h-3" strokeWidth={1.5} />
                      {s.segment}
                    </span>
                    <span className="font-numbers text-sm tabular-nums text-text-primary">{s.clients}</span>
                  </div>
                  <div className="h-1 bg-border w-full overflow-hidden">
                    <div className="h-full bg-maroon transition-all duration-700" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Cities + Upcoming retainers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div data-testid="analyst-cities" className="bg-surface border border-border p-6 fade-up">
          <SectionTitle tag="Geography" title="Top Cities" />
          <div className="space-y-3">
            {topCities.length === 0 && !loading && (
              <p className="font-body text-sm text-text-secondary">No city data available.</p>
            )}
            {topCities.map(c => {
              const pct = (c.clients / maxCityClients) * 100;
              return (
                <div key={c.city} data-testid={`city-${c.city}`}>
                  <div className="flex justify-between mb-1">
                    <span className="font-body text-sm text-text-primary flex items-center gap-2">
                      <MapPin className="w-3 h-3 text-maroon" strokeWidth={1.5} />
                      {c.city}
                    </span>
                    <span className="font-numbers text-sm tabular-nums text-text-secondary">{c.clients} clients</span>
                  </div>
                  <div className="h-1 bg-border w-full overflow-hidden">
                    <div className="h-full bg-forest transition-all duration-700" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div data-testid="analyst-upcoming" className="bg-surface border border-border p-6 fade-up fade-up-d1">
          <SectionTitle
            tag="Pipeline"
            title="Retainers · Next 30 Days"
            action={
              <button
                data-testid="analyst-goto-retainers"
                onClick={() => navigate('/retainers')}
                className="font-body text-xs uppercase tracking-widest text-maroon hover:text-maroon-hover"
              >
                View All →
              </button>
            }
          />
          <div className="space-y-2 max-h-80 overflow-auto">
            {upcoming.length === 0 && !loading && (
              <p className="font-body text-sm text-text-secondary py-2">No retainers generating in the next 30 days.</p>
            )}
            {upcoming.map(item => (
              <div
                key={item.id}
                data-testid={`upcoming-${item.id}`}
                className="p-3 border border-border hover:border-maroon hover:bg-cream transition-colors"
              >
                <div className="flex items-baseline justify-between">
                  <p className="font-body text-sm font-medium text-text-primary truncate">
                    {item.client_name || item.template_name}
                  </p>
                  <ChevronRight className="w-3 h-3 text-text-secondary flex-shrink-0" strokeWidth={1.5} />
                </div>
                <div className="flex items-baseline justify-between mt-1">
                  <p className="font-body text-[11px] text-text-secondary flex items-center gap-1">
                    <Repeat className="w-3 h-3" strokeWidth={1.5} />
                    {item.template_name} · in {item.days_until_due}d · {item.frequency}
                  </p>
                  <p className="font-numbers text-xs text-text-primary tabular-nums">{formatCurrency(item.monthly_amount)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer nav helpers */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <button
          data-testid="analyst-nav-intel"
          onClick={() => navigate('/client-intelligence')}
          className="bg-surface border border-border hover:border-maroon hover:bg-cream transition-colors p-5 text-left group"
        >
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-maroon" strokeWidth={1.5} />
            <p className="font-body text-xs uppercase tracking-[0.2em] text-maroon">Explore</p>
          </div>
          <p className="font-heading text-lg text-text-primary group-hover:text-maroon transition-colors">Client Intelligence →</p>
          <p className="font-body text-xs text-text-secondary mt-1">Maps, segments, growth trends and risk signals.</p>
        </button>
        <button
          data-testid="analyst-nav-retainers"
          onClick={() => navigate('/retainers')}
          className="bg-surface border border-border hover:border-maroon hover:bg-cream transition-colors p-5 text-left group"
        >
          <div className="flex items-center gap-2 mb-2">
            <Repeat className="w-4 h-4 text-maroon" strokeWidth={1.5} />
            <p className="font-body text-xs uppercase tracking-[0.2em] text-maroon">Manage</p>
          </div>
          <p className="font-heading text-lg text-text-primary group-hover:text-maroon transition-colors">Retainers →</p>
          <p className="font-body text-xs text-text-secondary mt-1">Recurring templates and monthly generation schedule.</p>
        </button>
        <button
          data-testid="analyst-nav-clients"
          onClick={() => navigate('/clients')}
          className="bg-surface border border-border hover:border-maroon hover:bg-cream transition-colors p-5 text-left group"
        >
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-maroon" strokeWidth={1.5} />
            <p className="font-body text-xs uppercase tracking-[0.2em] text-maroon">Directory</p>
          </div>
          <p className="font-heading text-lg text-text-primary group-hover:text-maroon transition-colors">Clients →</p>
          <p className="font-body text-xs text-text-secondary mt-1">Full client roster with segment and city metadata.</p>
        </button>
      </div>
    </Layout>
  );
};

export default AnalystDashboard;
